import type { Addon, DetailedPriceCharge } from '@cvo/domain';

export type CartItemType = 'fixed' | 'percentage';

/**
 * Base cart item properties.
 * Use with intersection types to extend: BaseCartItem & { customProp: value }
 */
export type BaseCartItem = {
  id: string;
  group: string;
  name: string;
  currency: string;
  description?: string;
  type: CartItemType;
  position?: number;
  /** Regular price when `value` is promotional; rendered struck through. */
  originalValue?: number;
  /** Marks the item as promotionally discounted (renders the promo tag). */
  promo?: boolean;
};

/**
 * Safe metadata type that excludes BaseCartItem keys to prevent overriding core properties.
 */
export type DefaultCartMeta = Omit<Record<string, unknown>, keyof BaseCartItem>;

/**
 * Fixed price cart item. Can be extended with generic metadata.
 * @example FixedCartItem<{ recurrent: boolean }>
 */
export type FixedCartItem<TExtra extends DefaultCartMeta = DefaultCartMeta> = TExtra & BaseCartItem & {
  type: 'fixed';
  value: number | string;
  quantity: number | null;
};

/**
 * Percentage-based cart item. Can be extended with generic metadata.
 * @example PercentageCartItem<{ recurrent: boolean }>
 */
export type PercentageCartItem<TExtra extends DefaultCartMeta = DefaultCartMeta> = TExtra & BaseCartItem & {
  type: 'percentage';
  rate: number;
  baseGroup: string;
};

/**
 * Union of all allowed cart item types.
 * Preserves discriminated union when using generics.
 * @example AllowedCartItem<{ recurrent: boolean }>
 */
export type AllowedCartItem<TExtra extends DefaultCartMeta = DefaultCartMeta> =
  | FixedCartItem<TExtra>
  | PercentageCartItem<TExtra>;

export type CartItemTypes = AllowedCartItem['type'];

// Shared cart model types
export type ChargeAggregate = Omit<DetailedPriceCharge, 'amount'> & { amount: number };
export type AddonWithQuantity = Addon & { quantity: number };

export function isFixedCartItem<TExtra extends DefaultCartMeta = DefaultCartMeta>(cartItem: AllowedCartItem<TExtra>): cartItem is FixedCartItem<TExtra> {
  return cartItem.type === 'fixed';
}

export function isPercentageCartItem<TExtra extends DefaultCartMeta = DefaultCartMeta>(cartItem: AllowedCartItem<TExtra>): cartItem is PercentageCartItem<TExtra> {
  return cartItem.type === 'percentage';
}

export const SUBSCRIPTION_FEES_ID = 'subscription-fee';